import { useState } from 'react';
import { Link } from 'react-router-dom';
import { get, post } from '../api';
import { DeactivatedName } from '../components/DeactivatedName';
import { Problems } from '../components/Field';
import { describe } from '../errors';
import { useLoad } from '../hooks';

interface Person {
  id: number;
  display_name: string;
  roles: string[];
  email: string;
}

/** Everything switched off, in one place, with a way back. Records are never deleted, so nothing here is lost. */
export function Deactivated() {
  const [tick, setTick] = useState(0);
  const [problems, setProblems] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const people = useLoad(
    () => get<{ items: Person[] }>('/api/contacts?state=inactive&limit=200'),
    [tick],
  );

  async function restore(p: Person) {
    setProblems([]);
    setMessage('');
    try {
      await post(`/api/contacts/${p.id}/restore`);
      setMessage(`${p.display_name} is active again.`);
      setTick((t) => t + 1);
    } catch (e) {
      setProblems(describe(e).problems);
    }
  }

  const items = people.data?.items ?? [];
  return (
    <>
      <h2>Deactivated</h2>
      <p className="muted">
        People you have deactivated. Tags are restored from the <Link to="/tags">Tags</Link> screen.
      </p>
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      <Problems problems={problems} title="Could not restore." />
      {people.error && (
        <p role="alert" className="error">
          {people.error}
        </p>
      )}
      {people.loading && <p>Loading…</p>}
      {!people.loading && !people.error && items.length === 0 && (
        <p className="empty">Nothing is deactivated.</p>
      )}
      {items.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Roles</th>
              <th>Email</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id}>
                <td>
                  <DeactivatedName name={p.display_name} />
                </td>
                <td>{p.roles.join(', ')}</td>
                <td>{p.email}</td>
                <td className="actions">
                  <button type="button" className="quiet" onClick={() => void restore(p)}>
                    Restore
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
